import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { NavDrawer } from "./NavDrawer";
import { useNavDrawer } from "./useNavDrawer";
import { SectionRail } from "./SectionRail";
import { DeskRail } from "@/features/desk/components/DeskRail";
import { CredentialsRail } from "@/features/credentials/CredentialsRail";
import { Header } from "./Header";
import { CommandPalette } from "./CommandPalette";
import { ImpersonationBar } from "./ImpersonationBar";
import { ChatBot } from "@/components/chat/ChatBot";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useSettingsStore } from "@/store/settingsStore";
import { useAuthStore } from "@/store/authStore";
import { useBrandingFollowsIdentity } from "@/hooks/useBrandingFollowsIdentity";
import { useScopeHierarchy } from "@/hooks/useScopeHierarchy";
import { ScopeSelector } from "@/components/scope/ScopeSelector";
import { ScopedOutlet } from "@/components/scope/ScopedOutlet";
import { cn } from "@/lib/utils";
import { parseMainContentWidth } from "@/lib/constants/userPreferences";
import { useRemoteSessionGuard } from "@/hooks/useRemoteSessionGuard";
import { useRoleSync } from "@/hooks/useRoleSync";
import { isSplitPaneRoute } from "@/lib/constants/layout";

/** Routes whose content is a height-locked, full-width columned surface (its own scroll). */
export function MainLayout() {
  const { sidebarCollapsed } = useSettingsStore();
  const { pathname } = useLocation();
  // Exact match only: nested routes under /admin/organizations (the analytics
  // dashboards) are normal scrolling pages, not the height-locked columned surface.
  const splitPane = isSplitPaneRoute(pathname);
  const scope = useScopeHierarchy();
  const user = useAuthStore((s) => s.user);
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const mainLayout = parseMainContentWidth(user?.preferences);
  // Below `md` the sidebar is a drawer opened from the header.
  const nav = useNavDrawer();

  // Cross-tab sign-out lives in `AuthGuard`, which wraps three authenticated
  // routes this layout does not: pending-approval, recovery codes and MFA
  // disable. The poll stays here — those screens do not need a 30s check.
  useRemoteSessionGuard();
  useRoleSync();

  useEffect(() => {
    void fetchMe();
  }, [fetchMe]);

  // The signed-in person's branding, refetched when that person changes:
  // both ends of an impersonation happen without remounting this layout.
  useBrandingFollowsIdentity();

  return (
    <TooltipProvider>
      <CommandPalette />
      <ImpersonationBar />
      {/* Deliberately transparent. `body` already carries the canvas colour
          (globals.css `@apply bg-page-background`) plus the theme's ambient
          gradients and masked grid — repainting an opaque page-background here
          covered the whole viewport and hid both layers. */}
      {/* Shifted down by the impersonation bar's height (0px when hidden), so
          the bar adds to the page rather than covering the header. The value
          comes from the same CSS variable the sidebar reads, which is why the
          two can never disagree about how much room to leave. */}
      <div className="mt-[var(--impersonation-bar-h,0px)] flex h-[calc(100svh-var(--impersonation-bar-h,0px))] min-h-0 overflow-hidden">
        <Sidebar />
        <NavDrawer nav={nav} />

        {/* Offset by the fixed rail from `md` up; below it the rail is a
            drawer and the page has the full width. */}
        <div
          className={cn(
            "flex min-h-0 min-w-0 flex-1 flex-col transition-all duration-300 ease-in-out",
            sidebarCollapsed ? "md:ml-16" : "md:ml-64",
          )}
        >
          <Header nav={nav} />

          {/* Global scope bar — pinned between header and scroll area. Below
              `md` its pickers can be wider than a phone, so the band scrolls
              sideways on its own rather than pushing past the edge, where the
              shell's `overflow-hidden` cut the last picker off. */}
          <div className="shrink-0 max-md:overflow-x-auto">
            <ScopeSelector scope={scope} />
          </div>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden md:flex-row">
            {/* Contextual section rails. Siblings of <main> rather than
                page content, which is what makes them run the full height of
                the surface instead of stopping where a table stops. Each
                renders nothing outside its own section. Desk's and the
                credentials page's share one frame, `ContextRail`. Below `md`
                the column stacks, so a rail's phone bar sits above the page
                rather than beside it. */}
            <SectionRail />
            <DeskRail />
            <CredentialsRail />

            <main
              className={cn(
                "main-content-area flex min-h-0 min-w-0 flex-1 flex-col overscroll-y-contain",
                splitPane
                  ? // Height-locked columned surface: no page scroll, no max-width.
                    "overflow-hidden px-4 py-4 sm:px-6"
                  : cn(
                      "overflow-y-auto overflow-x-hidden px-4 py-5 sm:px-6 sm:py-6",
                      mainLayout === "wide"
                        ? "w-full max-w-none"
                        : "mx-auto w-full max-w-7xl",
                    ),
              )}
            >
              {/* The page, held while the address's `?org=` is read into the
                  scope bar so it never fetches for the previous organization. */}
              <ScopedOutlet />
            </main>
          </div>
        </div>

        <ChatBot />
      </div>
    </TooltipProvider>
  );
}
